/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FC, useContext } from 'react';
import { useNotifications } from '@/data/Content/Notifications';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { NotificationModal } from '@/components/content/Notifications/parts/NotificationModal';
import { SnackbarAlert } from '@/components/content/Notifications/parts/SnackbarAlert';
import { getNotificationTitle } from '@/components/content/Notifications/utils/getNotificationTitle';
import { SessionExpiredModal } from '@/components/content/Notifications/parts/SessionExpiredModal';
import { SessionErrorContext } from '@/data/context/sessionError';
import { DpSnackbar } from '@/components/content/Notifications/SnackBarNotification/DpSnackBarNotification';
import { ConfirmationDialog } from '@/components/content/ConfirmationDialog';
import { DpSnackUserAuthNotification } from '@/components/content/Notifications/SnackBarNotification/DpSnackUserAuthNotification';

const excludeMessages = [
	'FolioSap was not found.',
	'no tiene una devolución.',
	'UserId was not found.',
	'The following error occurred during processing: "{0}".',
	'The string did not match the expected pattern.',
];

export const Notifications: FC = () => {
	const { message, clearMessage } = useNotifications();
	const isMobile = useIsMobile();
	const { sessionError } = useContext(SessionErrorContext);

	const isExcluded = excludeMessages.some((msg) => message.text.toString().includes(msg));

	if (message.isAddToCart || isExcluded) {
		return null;
	}

	const severity = message.severity ?? 'info';
	const type = sessionError && sessionError.type;
	const messageKey = sessionError && sessionError.messageKey;

	if (
		message.text === 'El artículo se ha añadido correctamente a la lista "Guardar para después"'
	) {
		return (
			<DpSnackbar
				id={`${severity}-modal`}
				message={message.text}
				mobile={isMobile}
				onClear={clearMessage}
			/>
		);
	}
	if (['error', 'success'].includes(severity)) {
		const title = getNotificationTitle(severity, message?.title);

		return (
			<NotificationModal
				id={`${severity}-modal`}
				title={title}
				message={message.text}
				mobile={isMobile}
				onClear={clearMessage}
			/>
		);
	}

	if (severity === 'snackbar') {
		return (
			<SnackbarAlert
				id={`${severity}-snackbar`}
				message={message.text}
				anchorOrigin={message.anchorOrigin}
				severity={message.snackbarSeverity}
				onClear={clearMessage}
			/>
		);
	}

	if (type === 'session-error' && messageKey === 'TimeoutMsg') {
		return (
			<SessionExpiredModal
				id={`sesion-expirada-modal`}
				title={'TU SESIÓN HA EXPIRADO'}
				message={
					'Se ha excedido el tiempo de espera de la sesión y se ha cerrado correctamente. Vuelva a iniciarla para acceder a su cuenta.'
				}
				mobile={isMobile}
			/>
		);
	}

	if (severity === 'confirmation' && message.confirmationDialog) {
		return (
			<ConfirmationDialog
				open
				text={message.confirmationDialog.body}
				onConfirm={message.confirmationDialog.onConfirm}
				onCancel={message.confirmationDialog.onCancel}
			/>
		);
	}

	if (severity === 'successAuth') {
		return (
			<DpSnackUserAuthNotification
				message={message.text.toString()}
				onClear={clearMessage}
				flow="login"
			/>
		);
	}

	return null;
};
